import { useState } from "react"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import Button from "@/v2-backoffice-client/components/button"
import { useLayerAiFork } from "@/v2-backoffice-client/layer/ai/layer.ai.fork"
import type {
  LayerAiChatTurn,
  LayerAiMode,
} from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type {
  LayerAiFieldSuggestion,
  LayerAiForkProposal,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"

export type LayerAiForkCardProps = {
  /** Initial values; the operator can edit both before forking. */
  proposal: LayerAiForkProposal
  featureCode: string
  /** Current Configuration editor draft, carried onto the fork. */
  draft: string
  /** Non-dismissed suggestions, landed on the fork. */
  activeSuggestions: LayerAiFieldSuggestion[]
  /**
   * When a Configuration suggestion is still pending it wins over the
   * draft — it was left unapplied deliberately, and the fork is where it
   * should land.
   */
  hasPendingConfiguration: boolean
  /** The running conversation, handed over to the fork's assistant. */
  mode: LayerAiMode | null
  turns: LayerAiChatTurn[]
  /** "Apply here instead": keep working on this layer. */
  onDismiss(): void
  /** The fork exists; clean up. Runs BEFORE the sheet navigates to it. */
  onForked(): void
}

/**
 * The assistant's fork proposal card: copies the layer with the current
 * draft and the non-dismissed suggestions onto a new feature code, then
 * opens the fork with this conversation continuing there.
 */
function LayerAiForkCard(props: LayerAiForkCardProps) {
  const {
    proposal,
    featureCode,
    draft,
    activeSuggestions,
    hasPendingConfiguration,
    mode,
    turns,
    onDismiss,
    onForked,
  } = props

  const [fork, setFork] = useState(proposal)

  const { forkAndApply, isForking } = useLayerAiFork({
    featureCode,
    mode,
    turns,
    onForked,
  })

  async function submit() {
    // Dismissed suggestions stay out of the fork; applied ones go in too —
    // they describe the same direction the fork is being created for.
    const blobUpdates: Record<string, unknown> = {}
    for (const suggestion of activeSuggestions) {
      blobUpdates[suggestion.Field] = JSON.parse(suggestion.Value)
    }

    // The editor draft is the operator's current working state — applied
    // suggestions plus their own manual tweaks — and may be unsaved; the
    // backend fork only copies SAVED blobs, so without this the fork
    // silently reverts everything previewed on the map.
    if (!hasPendingConfiguration) {
      try {
        blobUpdates.Configuration = JSON.parse(draft)
      } catch {
        // Unparsable draft: the fork keeps the saved configuration.
      }
    }

    await forkAndApply({ proposal: fork, blobUpdates })
  }

  return (
    <div className="flex shrink-0 flex-col gap-1.5 rounded-md border border-primary/40 bg-primary/5 p-2">
      <p className="flex items-center gap-1.5 text-xs font-medium">
        <Icon src="code-fork" size={12} className="text-primary" />
        Fork proposed
        <span className="font-normal text-muted-foreground">
          — this direction repurposes the layer
        </span>
      </p>
      <div className="flex flex-col gap-1">
        <Input
          aria-label="Fork feature code"
          className="h-7 font-mono text-[11px]"
          value={fork.FeatureCode}
          onChange={(event) =>
            setFork({ ...fork, FeatureCode: event.target.value })
          }
        />
        <Input
          aria-label="Fork name"
          className="h-7 text-xs"
          value={fork.Name}
          onChange={(event) => setFork({ ...fork, Name: event.target.value })}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Copies this layer with your current draft
        {activeSuggestions.length > 0
          ? " and the non-dismissed suggestions below"
          : ""}
        , texts and categories included, then opens the fork — with this
        conversation continuing there. This layer stays untouched.
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          size="tiny"
          iconSrc="code-fork"
          isLoading={isForking}
          disabled={!fork.FeatureCode.trim() || !fork.Name.trim()}
          data-feature-code="UI_BackofficeLayers_AiAssistant_Fork"
          onClick={() => void submit()}
        >
          Fork &amp; apply
        </Button>
        <Button
          size="tiny"
          variant="ghost"
          title="Keep working on this layer instead"
          data-feature-code="UI_BackofficeLayers_AiAssistant_ForkDismiss"
          onClick={onDismiss}
        >
          Apply here instead
        </Button>
      </div>
    </div>
  )
}

export default LayerAiForkCard
